import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../api";
import { 
  FiShoppingBag, 
  FiTool, 
  FiHome, 
  FiSearch, 
  FiUsers, 
  FiCheckCircle,
  FiTrendingUp,
  FiShield,
  FiStar
} from "react-icons/fi";
import { FaSearch, FaStar } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE || "";

export default function Home() {
  const nav = useNavigate();
  const { user } = useContext(AuthContext);
  const isAdmin = useMemo(() => {
    const email = user?.email ? user.email.toLowerCase().trim() : "";
    return (
      email === "prateekmishrachs@gmail.com" ||
      email === "prateekmishrachs@gmail..com"
    );
  }, [user?.email]);

  const [query, setQuery] = useState("");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [adConfig, setAdConfig] = useState(null);
  const [adLoading, setAdLoading] = useState(true);
  const [adError, setAdError] = useState("");

  const [highlights, setHighlights] = useState({
    rooms: [],
    items: [],
    workers: [],
  });
  const [highlightsLoading, setHighlightsLoading] = useState(true);
  const [highlightsError, setHighlightsError] = useState("");

  const options = [
    {
      title: "Buy / Rent / Sell",
      path: "/market",
      variant: "success",
      Icon: FiShoppingBag,
    },
    { title: "Hire Workers", path: "/workers", variant: "info", Icon: FiTool },
    {
      title: "Rent / Get a Room",
      path: "/rooms",
      variant: "warning",
      Icon: FiHome,
    },
  ];

  useEffect(() => {
    let ignore = false;

    const fetchAd = async () => {
      setAdLoading(true);
      setAdError("");
      try {
        const res = await api.get("/ads");
        if (!ignore) {
          setAdConfig(res.data?.ad || null);
        }
      } catch (err) {
        console.error("Failed to fetch advertisement:", err);
        if (!ignore) {
          setAdConfig(null);
          setAdError(
            err.response?.data?.message || "Unable to load advertisement."
          );
        }
      } finally {
        if (!ignore) {
          setAdLoading(false);
        }
      }
    };

    fetchAd();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const fetchHighlights = async () => {
      setHighlightsLoading(true);
      setHighlightsError("");
      try {
        const res = await api.get("/home/highlights");
        if (ignore) {
          return;
        }
        const payload = res.data?.data || {};
        setHighlights({
          rooms: Array.isArray(payload.rooms) ? payload.rooms : [],
          items: Array.isArray(payload.items) ? payload.items : [],
          workers: Array.isArray(payload.workers) ? payload.workers : [],
        });
      } catch (err) {
        console.error("Home highlights error:", err);
        if (!ignore) {
          setHighlights({ rooms: [], items: [], workers: [] });
          setHighlightsError("Unable to load featured listings right now.");
        }
      } finally {
        if (!ignore) {
          setHighlightsLoading(false);
        }
      }
    };

    fetchHighlights();
    return () => {
      ignore = true;
    };
  }, []);

  const formatLocation = (city, state) => {
    const cleanCity = typeof city === "string" ? city.trim() : "";
    const cleanState = typeof state === "string" ? state.trim() : "";
    if (cleanCity && cleanState) {
      return `${cleanCity}, ${cleanState}`;
    }
    return cleanCity || cleanState || "Location coming soon";
  };

  const formatCurrency = (value) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return null;
    }
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(value);
    } catch (err) {
      return `₹${value}`;
    }
  };

  const resolveImageUrl = (raw) => {
    if (typeof raw !== "string") {
      return null;
    }
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    if (trimmed.startsWith("/uploads/")) {
      return `${API_BASE_URL}${trimmed}`;
    }
    if (trimmed.startsWith("uploads/")) {
      return `${API_BASE_URL}/${trimmed}`;
    }
    return trimmed;
  };

  const embedHtml =
    typeof adConfig?.embedHtml === "string" ? adConfig.embedHtml.trim() : "";
  const bannerUrlRaw =
    typeof adConfig?.bannerUrl === "string" ? adConfig.bannerUrl.trim() : "";
  const destinationUrl = adConfig?.destinationUrl?.trim() || "";
  const hasEmbed = embedHtml.length > 0;
  const hasBanner = bannerUrlRaw.length > 0;

  const bannerUrl = hasBanner
    ? bannerUrlRaw.startsWith("http")
      ? bannerUrlRaw
      : `${process.env.REACT_APP_API_BASE_URL || ""}${bannerUrlRaw}`
    : "";

  const adEntries = useMemo(() => {
    if (!adConfig || adLoading || adError) {
      return [];
    }

    const baseEntry = {
      id: `sponsored-${adConfig._id || "ad"}`,
      title: adConfig.headline?.trim() || "Sponsored",
      subtitle: adConfig.destinationUrl
        ? (() => {
            try {
              return new URL(adConfig.destinationUrl).host;
            } catch (_err) {
              return "Advertisement";
            }
          })()
        : "Advertisement",
      metaPrimary:
        adConfig.ctaLabel || (destinationUrl ? "View offer" : "Learn more"),
      metaSecondary:
        adConfig.description?.trim().slice(0, 90) || "Promoted listing",
      chip: "Ad",
      sellerPrime: false,
      path: destinationUrl || "#",
      isAd: true,
    };

    const variants = [];

    if (hasEmbed) {
      variants.push({
        ...baseEntry,
        id: `${baseEntry.id}-embed`,
        embedHtml,
        image: null,
      });
    }

    if (hasBanner) {
      variants.push({
        ...baseEntry,
        id: `${baseEntry.id}-banner`,
        image: bannerUrl,
        embedHtml: "",
      });
    }

    if (variants.length === 0) {
      variants.push({
        ...baseEntry,
        image: bannerUrl || null,
        embedHtml: "",
      });
    }

    return variants;
  }, [
    adConfig,
    adLoading,
    adError,
    destinationUrl,
    hasEmbed,
    hasBanner,
    embedHtml,
    bannerUrl,
  ]);

  const spotlightEntries = useMemo(() => {
    const safeHighlights = highlights || {};
    const rooms = Array.isArray(safeHighlights.rooms)
      ? safeHighlights.rooms.slice(0, 4)
      : [];
    const items = Array.isArray(safeHighlights.items)
      ? safeHighlights.items.slice(0, 4)
      : [];
    const workers = Array.isArray(safeHighlights.workers)
      ? safeHighlights.workers.slice(0, 4)
      : [];

    const toRoomEntry = (room) => {
      const title = room?.title || "Room listing";
      const location = formatLocation(room?.city, room?.state);
      const price = formatCurrency(room?.price);
      const bedBathParts = [];
      if (typeof room?.beds === "number") {
        bedBathParts.push(`${room.beds} bed${room.beds === 1 ? "" : "s"}`);
      }
      if (typeof room?.baths === "number") {
        bedBathParts.push(`${room.baths} bath${room.baths === 1 ? "" : "s"}`);
      }

      return {
        id: room?.id || room?._id || title,
        title,
        subtitle: location,
        metaPrimary: price ? `${price}/month` : "Price on request",
        metaSecondary: bedBathParts.join(" • "),
        sellerPrime: Boolean(room?.sellerPrime),
        chip: "Room",
        path: "/rooms",
        image: resolveImageUrl(room?.image),
      };
    };

    const toItemEntry = (item) => {
      const title = item?.name || "Marketplace item";
      const location = formatLocation(item?.city, item?.state);
      const price = formatCurrency(item?.price);
      const type =
        typeof item?.type === "string" ? item.type.toLowerCase() : "";
      const chip =
        type === "sale"
          ? "For Sale"
          : type === "rent"
          ? "For Rent"
          : "Marketplace";

      return {
        id: item?.id || item?._id || title,
        title,
        subtitle: location,
        metaPrimary: price || "Ask for price",
        metaSecondary: "",
        sellerPrime: Boolean(item?.sellerPrime),
        chip,
        path: "/market",
        image: resolveImageUrl(item?.image),
      };
    };

    const toWorkerEntry = (worker) => {
      const title = worker?.name || "Professional";
      const location = formatLocation(worker?.city, worker?.state);
      const rate = formatCurrency(worker?.hourlyRate);
      const experience =
        typeof worker?.experience === "number"
          ? `${worker.experience} yr${worker.experience === 1 ? "" : "s"}`
          : "";
      const category =
        typeof worker?.category === "string" ? worker.category : "";
      const chip = category
        ? category
            .split("-")
            .map((segment) =>
              segment.length
                ? segment[0].toUpperCase() + segment.slice(1)
                : segment
            )
            .join(" ")
        : "Worker";

      return {
        id: worker?.id || worker?._id || title,
        title,
        subtitle: location,
        metaPrimary: rate ? `${rate}/hr` : "Rate on request",
        metaSecondary: experience ? `Experience: ${experience}` : "",
        sellerPrime: Boolean(worker?.sellerPrime),
        verified: Boolean(worker?.verified),
        chip,
        path: "/workers",
        image: resolveImageUrl(worker?.image),
      };
    };

    const baseEntries = [
      ...rooms.map(toRoomEntry),
      ...items.map(toItemEntry),
      ...workers.map(toWorkerEntry),
    ];

    const nonAdEntries =
      baseEntries.length <= 1
        ? baseEntries
        : (() => {
            const shuffled = [...baseEntries];
            for (let i = shuffled.length - 1; i > 0; i -= 1) {
              const j = Math.floor(Math.random() * (i + 1));
              [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            return shuffled;
          })();

    const ads = Array.isArray(adEntries) ? adEntries : [];
    if (!ads.length) {
      return nonAdEntries;
    }

    if (!nonAdEntries.length) {
      return ads;
    }

    const merged = [...nonAdEntries];
    const spacing = Math.floor(merged.length / (ads.length + 1)) + 1;

    ads.forEach((ad, index) => {
      const insertAt = Math.min(index * spacing + 1, merged.length + index);
      merged.splice(insertAt, 0, ad);
    });

    return merged;
  }, [highlights, adEntries]);

  return (
    <>
      <div className="hero-section-simple">
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col lg={8} xl={7}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center"
              >
                <motion.h1
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="hero-title-simple mb-3"
                >
                  Welcome to Rentalo
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="hero-subtitle-simple mb-4"
                >
                  Your one-stop rental marketplace for everything you need
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="search-section-simple"
                >
                  <Form
                    onSubmit={async (event) => {
                      event.preventDefault();
                      setError("");
                      setLoading(true);
                      try {
                        const res = await api.get("/items", {
                          params: { q: query },
                        });
                        setItems(res.data.items);
                      } catch (err) {
                        setError("Search failed.");
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <div className="search-input-simple-wrapper mb-3">
                      <span className="search-icon-simple">
                        <FaSearch />
                      </span>
                      <Form.Control
                        type="search"
                        placeholder="Search rooms, workers, items..."
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        className="search-input-simple"
                      />
                    </div>
                    <div className="text-start">
                      <Button
                        variant="primary"
                        className="search-btn-simple"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Searching...
                          </>
                        ) : (
                          "Search"
                        )}
                      </Button>
                    </div>
                  </Form>

                  <div className="text-center mt-3">
                    <span className="text-muted small">
                      Or{" "}
                      <span
                        className="link-simple"
                        role="button"
                        tabIndex={0}
                        onClick={() => nav("/market")}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            nav("/market");
                          }
                        }}
                      >
                        explore the marketplace
                      </span>
                    </span>
                  </div>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>

      {loading && (
        <div className="text-center mt-5">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {error && (
        <Container>
          <div className="alert alert-danger text-center rounded-pill mt-4">
            {error}
          </div>
        </Container>
      )}

      {items && items.length > 0 && (
        <Container className="mb-5 mt-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h4 className="mb-4 text-gradient fw-bold d-flex align-items-center">
              <FiSearch className="me-2" />
              Search Results
            </h4>
            <Row xs={1} md={3} className="g-4">
              {items.map((item) => (
                <Col key={item.id || item._id}>
                  <Card className="h-100 shadow-hover border-0 rounded-4 overflow-hidden">
                    <Card.Body className="p-4">
                      <Card.Title className="fw-bold mb-2">{item.name}</Card.Title>
                      <Card.Text className="text-secondary mb-0">
                        {item.description || "No description available"}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      )}

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, type: "spring" }}
      >
        <Container className="my-5 py-5">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3 text-gradient">
              Explore Our Services
            </h2>
            <p className="text-secondary mb-0">
              Everything you need in one place
            </p>
          </div>
          <Row xs={1} md={3} className="g-4">
            {options.map((option, index) => {
              const { title, path, variant, Icon } = option;
              return (
                <Col key={title}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: index * 0.15,
                      duration: 0.5,
                      type: "spring",
                    }}
                    whileHover={{
                      scale: 1.05,
                      y: -8,
                    }}
                  >
                    <Card
                      className="h-100 text-center shadow-hover border-0 feature-card-enhanced"
                      style={{ cursor: "pointer" }}
                      onClick={() => nav(path)}
                    >
                      <Card.Body className="p-5">
                        <div className="feature-icon-wrapper mb-4">
                          <div className={`feature-icon-bg bg-${variant}`}>
                            <Icon size={48} className="feature-icon" />
                          </div>
                        </div>
                        <Card.Title className="mt-3 fw-bold fs-4 mb-3">
                          {title}
                        </Card.Title>
                        <Card.Text className="feature-desc mt-3 mb-4">
                          {title === "Buy / Rent / Sell"
                            ? "Browse, buy, rent, or sell items and properties with ease."
                            : title === "Hire Workers"
                            ? "Find trusted professionals for any job or project."
                            : "Find or rent rooms and accommodations easily."}
                        </Card.Text>
                        <Button
                          variant={variant}
                          className="mt-2 px-5 py-2 shadow-hover rounded-pill fw-semibold"
                          onClick={(e) => {
                            e.stopPropagation();
                            nav(path);
                          }}
                        >
                          Explore <FiTrendingUp className="ms-2" />
                        </Button>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, type: "spring" }}
      >
        <Container className="my-5 py-5">
          <div className="about-bg rounded-5 p-5">
            <div className="text-center mb-5">
              <h2 className="fw-bold mb-3 text-gradient">
                How Rentalo Works
              </h2>
              <p className="text-secondary mb-0">
                Get started in three simple steps
              </p>
            </div>
            <Row className="g-4 justify-content-center">
              <Col md={4} className="text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="about-step-icon mb-4 mx-auto"
                >
                  <div className="step-number">1</div>
                  <div className="step-icon-wrapper bg-primary">
                    <FiSearch size={32} className="step-icon" />
                  </div>
                </motion.div>
                <h5 className="fw-bold mb-3">Discover</h5>
                <p className="text-secondary mb-0">
                  Browse thousands of rooms, workers, and items for rent or sale in your area.
                </p>
              </Col>
              <Col md={4} className="text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="about-step-icon mb-4 mx-auto"
                >
                  <div className="step-number">2</div>
                  <div className="step-icon-wrapper bg-success">
                    <FiUsers size={32} className="step-icon" />
                  </div>
                </motion.div>
                <h5 className="fw-bold mb-3">Connect</h5>
                <p className="text-secondary mb-0">
                  Contact verified owners and professionals directly and securely through our platform.
                </p>
              </Col>
              <Col md={4} className="text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="about-step-icon mb-4 mx-auto"
                >
                  <div className="step-number">3</div>
                  <div className="step-icon-wrapper bg-warning">
                    <FiCheckCircle size={32} className="step-icon" />
                  </div>
                </motion.div>
                <h5 className="fw-bold mb-3">Enjoy</h5>
                <p className="text-secondary mb-0">
                  Rent, hire, or buy with confidence and enjoy peace of mind with our secure platform.
                </p>
              </Col>
            </Row>
          </div>
        </Container>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, type: "spring", delay: 0.1 }}
      >
        <Container className="my-5 py-5">
          <div className="spotlight-section-wrapper">
            <div className="text-center mb-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="spotlight-section-title mb-3">Fresh On Rentalo</h2>
                <p className="spotlight-section-subtitle mb-0">
                  A quick look at the latest rooms, marketplace goods, and skilled
                  workers.
                </p>
              </motion.div>
            </div>

          {highlightsError && (
            <Alert
              variant="warning"
              className="home-spotlight-alert text-center shadow-sm"
            >
              {highlightsError}
            </Alert>
          )}

          {highlightsLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" size="lg" />
              <p className="text-muted mt-3">Loading featured listings...</p>
            </div>
          ) : spotlightEntries.length === 0 ? (
            <div className="text-center py-5">
              <div className="empty-state-icon mb-3">
                <FiTrendingUp size={48} className="text-muted" />
              </div>
              <p className="text-muted mb-0 fs-5">
                No featured listings are available right now.
              </p>
            </div>
          ) : (
            <Row className="g-4 align-items-stretch">
              {spotlightEntries.map((entry, index) => {
                const entryPath =
                  typeof entry.path === "string" ? entry.path.trim() : "";
                const isAdEntry = Boolean(entry.isAd);
                const isExternalLink = /^https?:\/\//i.test(entryPath);
                const isNavigable =
                  entryPath &&
                  entryPath !== "#" &&
                  (isExternalLink || entryPath.startsWith("/"));

                const handleActivate = () => {
                  if (!isNavigable) {
                    return;
                  }
                  if (isAdEntry && isExternalLink) {
                    if (typeof window !== "undefined") {
                      window.open(entryPath, "_blank", "noopener,noreferrer");
                    }
                    return;
                  }
                  nav(entryPath);
                };

                const handleKeyDown = (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleActivate();
                  }
                };

                return (
                  <Col
                    xs={12}
                    sm={6}
                    lg={4}
                    xl={3}
                    key={entry.id || entry.title}
                    className="d-flex"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      className="w-100"
                      whileHover={{ y: -8 }}
                    >
                      <Card
                        className={`spotlight-card w-100 h-100${
                          isNavigable ? " spotlight-card-clickable" : ""
                        }`}
                        role={isNavigable ? "button" : undefined}
                        tabIndex={isNavigable ? 0 : -1}
                        onClick={isNavigable ? handleActivate : undefined}
                        onKeyDown={isNavigable ? handleKeyDown : undefined}
                      >
                        <div className="spotlight-card-image-wrapper">
                          {entry.embedHtml ? (
                            <div
                              className="spotlight-card-embed"
                              dangerouslySetInnerHTML={{
                                __html: entry.embedHtml,
                              }}
                            />
                          ) : entry.image ? (
                            <img
                              src={entry.image}
                              alt={entry.title}
                              className="spotlight-card-image"
                            />
                          ) : (
                            <div className="spotlight-card-placeholder">
                              <div className="spotlight-placeholder-icon">
                                {(entry.chip || entry.title || "?")
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                            </div>
                          )}
                          {(entry.chip || entry.sellerPrime || entry.verified || isAdEntry) && (
                            <div className="spotlight-card-badges">
                              {entry.chip && (
                                <span className="spotlight-badge spotlight-badge-category">
                                  {entry.chip}
                                </span>
                              )}
                              {entry.verified && (
                                <span className="spotlight-badge spotlight-badge-verified">
                                  <FiCheckCircle className="me-1" size={12} />
                                  Verified
                                </span>
                              )}
                              {entry.sellerPrime && (
                                <span className="spotlight-badge spotlight-badge-prime">
                                  <FiStar className="me-1" size={12} />
                                  Prime
                                </span>
                              )}
                              {isAdEntry && (
                                <span className="spotlight-badge spotlight-badge-ad">
                                  Sponsored
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <Card.Body className="spotlight-card-body">
                          {entry.metaPrimary &&
                            (isAdEntry ? (
                              <div className="spotlight-ad-cta text-primary fw-semibold mb-2">
                                {entry.metaPrimary}
                              </div>
                            ) : (
                              <div className="spotlight-price mb-2">
                                {entry.metaPrimary}
                              </div>
                            ))}
                          <Card.Title className="spotlight-card-title mb-2">
                            {entry.title}
                          </Card.Title>
                          {entry.metaSecondary && (
                            <p className="spotlight-meta text-muted mb-2 small">
                              {entry.metaSecondary}
                            </p>
                          )}
                          <div className="spotlight-location d-flex align-items-center">
                            <span className="text-muted small">
                              {entry.subtitle}
                            </span>
                          </div>
                          {isAdEntry && isAdmin && (
                            <button
                              type="button"
                              className="btn btn-link p-0 mt-2 spotlight-ad-manage text-decoration-none"
                              onClick={(event) => {
                                event.stopPropagation();
                                nav("/admin/ads");
                              }}
                              onKeyDown={(event) => event.stopPropagation()}
                            >
                              Manage ad setup
                            </button>
                          )}
                        </Card.Body>
                      </Card>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          )}
          </div>
        </Container>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, type: "spring", delay: 0.2 }}
      >
        <Container className="my-5 py-5">
          <Row className="align-items-center justify-content-center">
            <Col lg={10} xl={8}>
              <div className="testimonial-card-enhanced p-5 rounded-5">
                <div className="text-center mb-4">
                  <div className="d-flex align-items-center justify-content-center mb-3">
                    <img
                      src="https://randomuser.me/api/portraits/men/32.jpg"
                      alt="Amit Sharma"
                      className="testimonial-avatar"
                    />
                    <div className="ms-3 text-start">
                      <div className="fw-bold fs-5 text-dark">Amit Sharma</div>
                      <div className="text-muted small">Verified User</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className="text-warning me-1" />
                    ))}
                  </div>
                </div>
                <blockquote className="testimonial-quote text-center mb-4">
                  <p className="fs-5 fst-italic text-dark mb-0">
                    "Rentalo made it so easy to find a room and hire a reliable
                    worker. The platform is user-friendly and the listings are
                    verified. Highly recommended!"
                  </p>
                </blockquote>
                <div className="text-center">
                  <h4 className="fw-bold mb-3 text-gradient">
                    Trusted by 10,000+ users across India
                  </h4>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <span className="badge badge-feature bg-success">
                      <FiShield className="me-1" />
                      Verified Listings
                    </span>
                    <span className="badge badge-feature bg-info">
                      <FiCheckCircle className="me-1" />
                      Secure Payments
                    </span>
                    <span className="badge badge-feature bg-warning">
                      <FiStar className="me-1" />
                      24/7 Support
                    </span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </motion.div>

      <footer className="footer-enhanced mt-auto py-5 border-top">
        <Container>
          <div className="text-center">
            <p className="text-muted mb-2 mb-md-0">
              &copy; {new Date().getFullYear()} Rentalo. All rights reserved.
            </p>
            <p className="text-muted small mb-0">
              Made with ❤️ for India
            </p>
          </div>
        </Container>
      </footer>
    </>
  );
}
